import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { WorkflowApprovalReviewCard } from "@/components/workflows/WorkflowApprovalReviewCard";
import { workflowRunsService } from "@/services/workflowRunsService";
import { workflowEngineService } from "@/services/workflowEngine/workflowEngineService";
import { ensureGmailWriteAccess } from "@/lib/gmailAgentPermissions";
import { hasGmailModifyScope } from "@/lib/googleAuth";
import { resolveWorkflowForRun } from "@/lib/resolveWorkflowForRun";
import { formatRunStatus, formatRunStatusSubtitle } from "@/lib/workflowRunLabels";
import { findSpamClassification, getEmailPreviewFromRun } from "@/lib/workflowRunReview";
import type { Workflow } from "@/types/aiToolkit";
import type { WorkflowRun, WorkflowRunEvent } from "@/types/workflowEngine";
import {
  Pause,
  Play,
  SkipForward,
  MessageSquare,
  ExternalLink,
  Loader2,
} from "lucide-react";

interface WorkflowRunInspectorProps {
  runId: string;
  workflow?: Workflow;
  onClose?: () => void;
}

export function WorkflowRunInspector({ runId, workflow, onClose }: WorkflowRunInspectorProps) {
  const [expandedEvent, setExpandedEvent] = useState<string | null>(null);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [hasWriteScope, setHasWriteScope] = useState<boolean | null>(null);

  const { data: run, refetch: refetchRun } = useQuery({
    queryKey: ["workflow-run", runId],
    queryFn: () => workflowRunsService.getRun(runId),
    refetchInterval: (q) => {
      const status = (q.state.data as WorkflowRun | null)?.status;
      return status === "running" || status === "pending" || status === "waiting_approval"
        ? 2000
        : false;
    },
  });

  const { data: resolvedWorkflow } = useQuery({
    queryKey: ["workflow-for-run", runId, run?.workflowId, workflow?.id],
    queryFn: () => resolveWorkflowForRun({ propWorkflow: workflow, run: run! }),
    enabled: !!run,
  });

  const effectiveWorkflow =
    workflow?.nodes?.length ? workflow : resolvedWorkflow ?? undefined;

  const { data: events = [] } = useQuery({
    queryKey: ["workflow-run-events", runId],
    queryFn: () => workflowRunsService.listEvents(runId),
    refetchInterval:
      run?.status === "running" || run?.status === "waiting_approval" ? 2000 : false,
  });

  const { data: artifacts = [] } = useQuery({
    queryKey: ["workflow-run-artifacts", runId],
    queryFn: () => workflowRunsService.listArtifacts(runId),
    refetchInterval: run?.status === "running" ? 2000 : false,
  });

  const handlePause = async () => {
    if (!run?.currentNodeId) return;
    await workflowEngineService.pauseRun(runId, run.currentNodeId);
    refetchRun();
  };

  const handleResume = async () => {
    if (!effectiveWorkflow) return;
    await workflowEngineService.resumeRun(runId, effectiveWorkflow);
    refetchRun();
  };

  const handleSkip = async (nodeId: string) => {
    if (!effectiveWorkflow) return;
    await workflowEngineService.skipNode(runId, effectiveWorkflow, nodeId);
    refetchRun();
  };

  const handleForkChat = async (nodeId: string) => {
    const threadId = `workflow-fork-${runId}-${nodeId}`;
    await workflowEngineService.forkToChat(runId, nodeId, threadId);
    window.open(`/chat?thread=${threadId}`, "_blank");
    refetchRun();
  };

  const handleApproveTrash = async () => {
    if (!effectiveWorkflow || !run?.currentNodeId) return;
    setApprovalLoading(true);
    try {
      const writeOk = hasWriteScope ?? (await hasGmailModifyScope());
      if (!writeOk) {
        const granted = await ensureGmailWriteAccess();
        setHasWriteScope(granted);
        if (!granted) return;
      }
      const currentNode = effectiveWorkflow.nodes.find((n) => n.id === run.currentNodeId);
      if (currentNode?.type === "tool") {
        await workflowEngineService.approveToolAndContinue(
          runId,
          effectiveWorkflow,
          run.currentNodeId
        );
      } else {
        await workflowEngineService.approveHumanReviewAndContinue(
          runId,
          effectiveWorkflow,
          run.currentNodeId
        );
      }
      refetchRun();
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleKeepEmail = async () => {
    setApprovalLoading(true);
    try {
      await workflowEngineService.dismissApproval(runId);
      refetchRun();
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleReconnectGmail = async () => {
    setApprovalLoading(true);
    try {
      const granted = await ensureGmailWriteAccess();
      setHasWriteScope(granted);
    } finally {
      setApprovalLoading(false);
    }
  };

  if (!run) {
    return <div className="p-4 text-sm text-muted-foreground">Loading run...</div>;
  }

  const isWaitingApproval = run.status === "waiting_approval";
  const currentNode = effectiveWorkflow?.nodes.find((n) => n.id === run.currentNodeId);
  const currentStepLabel = currentNode?.data?.label as string | undefined;
  const statusSubtitle = formatRunStatusSubtitle(run.status, currentStepLabel);
  const spamVerdict = findSpamClassification(run);
  const emailPreview = getEmailPreviewFromRun(run);
  const showSkip = !isWaitingApproval;

  return (
    <div className="flex flex-col h-full border rounded-lg bg-background">
      <div className="flex items-center justify-between p-3 border-b">
        <div>
          <h3 className="font-semibold text-sm">Workflow Run</h3>
          <p className="text-xs text-muted-foreground font-mono">{run.id.slice(0, 8)}…</p>
          {statusSubtitle && (
            <p className="text-xs text-muted-foreground mt-0.5">{statusSubtitle}</p>
          )}
        </div>
        <Badge
          variant={
            run.status === "completed"
              ? "default"
              : run.status === "failed"
                ? "destructive"
                : isWaitingApproval
                  ? "outline"
                  : "secondary"
          }
        >
          {formatRunStatus(run.status)}
        </Badge>
      </div>

      {isWaitingApproval && (
        <WorkflowApprovalReviewCard
          email={emailPreview}
          verdict={spamVerdict}
          loading={approvalLoading}
          writeScopeMissing={hasWriteScope === false}
          onKeepEmail={() => void handleKeepEmail()}
          onMoveToTrash={() => void handleApproveTrash()}
          onReconnectGmail={() => void handleReconnectGmail()}
        />
      )}

      <div className="flex gap-2 p-2 border-b">
        {run.status === "running" && (
          <Button size="sm" variant="outline" onClick={handlePause}>
            <Pause className="h-3 w-3 mr-1" /> Pause
          </Button>
        )}
        {(run.status === "paused" || run.status === "waiting_chat") && effectiveWorkflow && (
          <Button size="sm" variant="outline" onClick={handleResume}>
            <Play className="h-3 w-3 mr-1" /> Resume
          </Button>
        )}
        {onClose && (
          <Button size="sm" variant="ghost" onClick={onClose}>
            Close
          </Button>
        )}
      </div>

      <Accordion
        type="single"
        collapsible
        defaultValue={isWaitingApproval ? undefined : "technical"}
        className="flex-1 min-h-0 px-2"
      >
        <AccordionItem value="technical" className="border-none">
          <AccordionTrigger className="text-sm py-3 hover:no-underline">
            Technical timeline
            {artifacts.length > 0 && (
              <span className="text-xs font-normal text-muted-foreground ml-2">
                ({events.length} events, {artifacts.length} artifacts)
              </span>
            )}
          </AccordionTrigger>
          <AccordionContent>
            <ScrollArea className={isWaitingApproval ? "h-[240px]" : "h-[400px]"}>
              <div className="space-y-2 pr-3 pb-2">
                {events.map((ev) => (
                  <EventRow
                    key={ev.id}
                    event={ev}
                    expanded={expandedEvent === ev.id}
                    onToggle={() => setExpandedEvent(expandedEvent === ev.id ? null : ev.id)}
                    onSkip={
                      showSkip && effectiveWorkflow
                        ? () => handleSkip(ev.nodeId!)
                        : undefined
                    }
                    onFork={ev.nodeType === "agent" ? () => handleForkChat(ev.nodeId!) : undefined}
                  />
                ))}
                {events.length === 0 && (
                  <p className="text-sm text-muted-foreground">No events yet.</p>
                )}

                {artifacts.length > 0 && (
                  <div className="pt-3 border-t space-y-2">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Artifacts
                    </p>
                    {artifacts.map((art) => (
                      <div key={art.id} className="border rounded-md p-3 text-sm">
                        <div className="flex items-center justify-between">
                          <Badge variant="outline">{art.artifactType}</Badge>
                          {art.url && (
                            <Button size="sm" variant="ghost" asChild>
                              <a href={art.url} target="_blank" rel="noreferrer">
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            </Button>
                          )}
                        </div>
                        <p className="font-medium mt-1">{art.title ?? art.id}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </ScrollArea>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}

function EventRow({
  event,
  expanded,
  onToggle,
  onSkip,
  onFork,
}: {
  event: WorkflowRunEvent;
  expanded: boolean;
  onToggle: () => void;
  onSkip?: () => void;
  onFork?: () => void;
}) {
  return (
    <div className="border rounded-md p-2 text-sm">
      <button type="button" className="w-full text-left" onClick={onToggle}>
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="secondary" className="text-xs">
            {event.eventType}
          </Badge>
          {event.nodeType && <span className="text-muted-foreground">{event.nodeType}</span>}
          {event.confidence != null && (
            <Badge variant="outline" className="text-xs">
              conf {(event.confidence * 100).toFixed(0)}%
            </Badge>
          )}
          {event.quality != null && (
            <Badge variant="outline" className="text-xs">
              qual {(event.quality * 100).toFixed(0)}%
            </Badge>
          )}
          {event.durationMs != null && (
            <span className="text-xs text-muted-foreground">{event.durationMs}ms</span>
          )}
        </div>
      </button>
      {expanded && event.outputSnapshot != null && (
        <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-auto max-h-32">
          {typeof event.outputSnapshot === "string"
            ? event.outputSnapshot
            : JSON.stringify(event.outputSnapshot, null, 2)}
        </pre>
      )}
      <div className="flex gap-1 mt-2">
        {onSkip && event.nodeId && (
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onSkip}>
            <SkipForward className="h-3 w-3 mr-1" /> Skip
          </Button>
        )}
        {onFork && (
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={onFork}>
            <MessageSquare className="h-3 w-3 mr-1" /> Open in Chat
          </Button>
        )}
      </div>
    </div>
  );
}
